import React, { useContext, useState } from "react";
import CustomTable from "../../components/CustomTable";
import CustomerAdd from "./CustomerAdd";
import useApi from "../../hooks/useApi";
import { deleteCustomer, getBranches, ListCustomer, StatusChangeCustomer, } from "../../constants/API/API";
import useFilter from "../../hooks/useFilter";
import CustomerUpdate from "./CustomerUpdate";
import usePermissions from "../../hooks/usePermissions";
import { permissionKeys } from "../../constants/staffRole";
import { AppStateContext } from "../../context/AppContext";
import moment from "moment";
import { EditOutlined, EyeOutlined, FilterFilled } from "@ant-design/icons";
import { Button, message, Select, Space, Switch } from "antd";
import CustomerView from "./CustomerView";
import DeleteRecord from "../../components/DeleteRecord";
import { useNavigate } from "react-router";

const Customer = () => {
  const navigate = useNavigate();
  const { isAdd, isEdit } = usePermissions({
    key: permissionKeys.Students,
    redirectPermissions: ["isView"],
  });
  const { setPageHeading } = useContext(AppStateContext);
  setPageHeading("User Management");
  const [loading, setLoading] = useState(true);
  const [refresh, setRefresh] = useState(new Date().getTime());
  const [addModal, setAddModal] = useState(false);
  const [editModal, setEditModal] = useState(false);
  const [viewModal, setViewModal] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [data, setData] = useState([]);
  const [branches, setBranches] = useState([]);
  const [totalDocs, setTotalDocs] = useState(0);
  const [filters, setFilters] = useFilter({ sortField: "createdAt", sort: "desc", startDate: null, endDate: null, roleType: "", });

  const addCustomer = () => {
    setAddModal(true);
  };

  const handleEditClick = (record) => {
    setSelectedCustomer(record);
    setEditModal(true);
  };

  useApi({
    callback: ListCustomer,
    payload: filters,
    dependencies: [refresh, filters],
    shouldCallApi: true,
    setLoading,
    onSuccess: ({ list, totalDocs }) => {
      setData(list);
      setTotalDocs(totalDocs);
      setLoading(false);
    },
    onError: () => {
      setLoading(false);
    },
  });

  useApi({
    callback: getBranches,
    dependencies: [],
    shouldCallApi: true,
    onSuccess: ({ data }) => {
      setBranches(data);
    },
    onError: () => {},
  });

  const handleStatusChange = async (checked, customer) => {
    console.log("Status change for customer:", customer._id, "to", checked);
    setLoading(true);
    try {
      await StatusChangeCustomer(customer._id, { isActive: checked });
      message.success(
        `Customer ${checked ? "activated" : "deactivated"} successfully`
      );
      setRefresh(new Date().getTime()); // Refresh list
    } catch (error) {
      message.error("Failed to update status");
      setLoading(false);
    }
  };

  const handleExport = async () => {
    try {
      setLoading(true);

      const res = await ListCustomer({
        ...filters,
        page: 1,
        limit: 10000,
      });

      const list = res?.data?.list || [];

      if (!list.length) {
        message.warning("No data to export");
        return;
      }

      const exportData = list.map((item) => ({
        Name: item.name || "-",
        Gender: item.gender ? item.gender.charAt(0).toUpperCase() + item.gender.slice(1) : "-",
        Email: item.email || "-",
        Mobile: item.countryCode
          ? `${item.countryCode.includes("+") ? "" : "+"}${item.countryCode} ${item.mobileNumber}`
          : item.mobileNumber || "-",
        Branch: item.branch?.name || "-",
        Status: item.isActive ? "Active" : "Inactive",
        "Registered Date": moment(item.createdAt).format("DD-MM-YYYY"),
      }));

      downloadCSV(exportData, "customers.csv");
    } catch (error) {
      console.log("Export error:", error);
      message.error("Failed to export data");
    } finally {
      setLoading(false);
    }
  };

  const downloadCSV = (data, filename) => {
    if (!data.length) return;

    const headers = Object.keys(data[0]).join(",");
    const rows = data.map((row) =>
      Object.values(row)
        .map((val) => `"${val}"`)
        .join(",")
    );

    const csvContent = [headers, ...rows].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const columns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      render: (_, item) => (
        <>
          <p>{item?.name}</p>
        </>
      ),
    },
    {
      title: "Gender",
      dataIndex: "gender",
      key: "gender",
      render: (text) => (text ? <span style={{ textTransform: "capitalize" }}>{text}</span> : "-"),
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
    },
    {
      title: "Mobile Number",
      dataIndex: "mobileNumber",
      key: "mobileNumber",
      render: (_, item) => (
        <>
          <p>
            {item?.countryCode
              ? `${item?.countryCode.includes("+") ? "" : "+"}${item?.countryCode
              }`
              : "Social Login"}{" "}
            {item?.mobileNumber}
          </p>
        </>
      ),
    },
    {
      title: "Branch",
      dataIndex: "branch",
      key: "branch",
      render: (_, record) => (
        <span>{record?.branch?.name || "-"}</span>
      ),
    },

    {
      title: "Role Type",
      dataIndex: "roleType",
      key: "roleType",
      // filters: [
      //   { text: "Student", value: "student" },
      //   { text: "Parent", value: "parent" },
      // ],
      onFilter: (value, record) => record.roleType === value,
      filterIcon: (filtered) => (
        <FilterFilled style={{ color: filtered ? "#1890ff" : undefined }} />
      ),
    },
    {
      title: "Status",
      dataIndex: "isActive",
      key: "isActive",
      render: (text, record) => (
        <Switch
          checked={record.isActive}
          onChange={(checked) => handleStatusChange(checked, record)}
          disabled={!isEdit}
        />
      ),
    },
    {
      title: "Created At",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (text, record) => moment(record.createdAt).format("ll"),
    },
    {
      title: "Updated At",
      dataIndex: "updatedAt",
      key: "updatedAt",
      render: (text, record) => moment(record.updatedAt).format("ll"),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <Space size="middle">
          <Button
            type="primary"
            onClick={() => {
              if (record?.roleType === "parent") {
                navigate(`/students/parent/view/${record._id}`);
              } else {
                navigate(`/students/view/${record._id}`);
              }
            }}
            icon={<EyeOutlined />}
            title="View"
          />

          <Button
            type="primary"
            onClick={() => handleEditClick(record)}
            icon={<EditOutlined />}
            title="Edit"
          />
          <DeleteRecord
            _id={record._id}
            callback={() => deleteCustomer(record._id, { isDeleted: true })}
            setLoading={setLoading}
            setRefresh={setRefresh}
            trigger={true}
          />
        </Space>
      ),
    },
  ];

  const handleBranchFilter = (value) => {
    setFilters((prev) => ({
      ...prev,
      branchId: value,
      page: 1,
    }));
  };

  const renderFilters = () => (
    <Space>
      <Select
        allowClear
        style={{ width: 200 }}
        placeholder="Filter by Branch"
        onChange={handleBranchFilter}
        value={filters.branchId}
      >
        <Select.Option value="">All Branches</Select.Option>
        {branches.map((branch) => (
          <Select.Option key={branch._id} value={branch._id}>
            {branch.name}
          </Select.Option>
        ))}
      </Select>

      <Button type="primary" onClick={handleExport}>
        Export Data
      </Button>

    </Space>
  );

  return (
    <div>

      <CustomTable
        addFn={addCustomer}
        data={data}
        loading={loading}
        filters={filters}
        setFilters={setFilters}
        columns={columns}
        totalDocs={totalDocs}
        customFilter={renderFilters}
        searchField={["name", "email", "mobileNumber"]}
        searchPlaceholder="Search by name, email or mobile number"
      />

      {addModal && isAdd && (
        <CustomerAdd
          visible={addModal}
          setVisible={setAddModal}
          refresh={setRefresh}
        />
      )}
      {editModal && isAdd && selectedCustomer && (
        <CustomerUpdate
          editVisible={editModal}
          setEditVisible={setEditModal}
          selectedCustomer={selectedCustomer}
          refresh={setRefresh}
        />
      )}
      {viewModal && (
        <CustomerView
          visible={viewModal}
          setVisible={setViewModal}
          id={selectedCustomer?._id}
        />
      )}
    </div>
  );
};

export default Customer;
